import { IconArrowRight } from "@tabler/icons-react";
import { Button } from "@onengine/component-library";
import type { ProposedAssignmentChange } from "@/api/form-builder/formAssistant.types";
import type {
  ApprovalStatus,
  ChangeApplyStatus,
  ChangeWithStatus,
} from "@/store/form-builder/useAssignmentsAssistantStore";
import panelStyles from "@/components/features/FormBuilder/Assistant/Assistant.module.scss";
import styles from "./AssignmentsAssistant.module.scss";

const ACTION_LABEL: Record<ProposedAssignmentChange["action"], string> = {
  create: "New assignment",
  replace: "Change form",
  remove: "Remove assignment",
};

export const CHANGE_STATUS_LABEL: Partial<Record<ChangeApplyStatus, string>> = {
  applying: "Applying",
  applied: "Applied",
  skipped: "Skipped",
  failed: "Failed, try again",
};

interface ProposedChangesCardProps {
  approval: ApprovalStatus;
  changes: ChangeWithStatus[];
  isBusy: boolean;
  onApprove: () => void;
  onCancel: () => void;
  onApply: (change: ChangeWithStatus) => void;
  onSkip: (change: ChangeWithStatus) => void;
  onApplyAll: () => void;
}

export function canApplyChange(change: ProposedAssignmentChange): boolean {
  if (change.action === "remove") return Boolean(change.rowId);
  if (!change.assignment.templateId) return false;
  return change.action === "create" || Boolean(change.rowId);
}

export function ChangeForms({ change }: { change: ProposedAssignmentChange }) {
  return (
    <span className={styles.changeForms}>
      {change.fromTemplateName && (
        <span className={styles.fromForm}>{change.fromTemplateName}</span>
      )}
      {change.fromTemplateName && change.toTemplateName && (
        <IconArrowRight size={14} aria-label="to" />
      )}
      {change.toTemplateName ? <strong>{change.toTemplateName}</strong> : null}
    </span>
  );
}

export function ProposedChangesCard({
  approval,
  changes,
  isBusy,
  onApprove,
  onCancel,
  onApply,
  onSkip,
  onApplyAll,
}: ProposedChangesCardProps) {
  const openCount = changes.filter(
    (c) => (c.status === "pending" || c.status === "failed") && canApplyChange(c),
  ).length;
  const isApplying = changes.some((c) => c.status === "applying");
  const isPlan = approval === "pending";

  return (
    <section className={panelStyles.proposalCard} aria-label="Proposed assignment changes">
      <div className={panelStyles.cardHeader}>
        <span className={panelStyles.cardTitle}>
          {changes.length === 1 ? "1 proposed change" : `${changes.length} proposed changes`}
        </span>
        {approval === "cancelled" && <span className={panelStyles.statusChip}>Cancelled</span>}
        {approval === "approved" && openCount > 1 && (
          <Button variant="primary" size="small" onClick={onApplyAll} isDisabled={isApplying}>
            {`Apply ${openCount}`}
          </Button>
        )}
      </div>
      <ul className={styles.changeList}>
        {changes.map((change) => (
          <li key={change.key} className={styles.changeRow}>
            <span className={panelStyles.cardMeta}>
              {ACTION_LABEL[change.action]}: {change.where}
            </span>
            <ChangeForms change={change} />
            {change.warning && <span className={styles.warning}>{change.warning}</span>}
            <span className={panelStyles.cardMeta}>{change.reason}</span>
            {approval === "approved" && (
              <div className={panelStyles.cardActions}>
                {!canApplyChange(change) && (
                  <span className={panelStyles.statusChip}>Cannot apply</span>
                )}
                {canApplyChange(change) &&
                (change.status === "pending" || change.status === "failed") ? (
                  <>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => onApply(change)}
                      isDisabled={isApplying}
                    >
                      {change.status === "failed" ? "Try again" : "Apply"}
                    </Button>
                    <Button
                      variant="tertiary"
                      size="small"
                      onClick={() => onSkip(change)}
                      isDisabled={isApplying}
                    >
                      Skip
                    </Button>
                  </>
                ) : null}
                {CHANGE_STATUS_LABEL[change.status] && (
                  <span className={panelStyles.statusChip}>
                    {CHANGE_STATUS_LABEL[change.status]}
                  </span>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {isPlan ? (
        <>
          <div className={panelStyles.cardActions}>
            <Button variant="primary" size="small" onClick={onApprove} isDisabled={isBusy}>
              Approve plan
            </Button>
            <Button variant="tertiary" size="small" onClick={onCancel} isDisabled={isBusy}>
              Cancel
            </Button>
          </div>
          <span className={panelStyles.cardMeta}>
            Or reply to change it. Nothing shows on the page until you approve.
          </span>
        </>
      ) : (
        approval === "approved" && (
          <span className={panelStyles.cardMeta}>
            Shown on the page in yellow. Nothing changes until you apply.
          </span>
        )
      )}
    </section>
  );
}
